import React from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import Header from '../../components/Header';
import CampaignList from './CampaignList';
import CampaignEditor from './CampaignEditor';
import HistoryTab from './HistoryTab';
import EvaluationTab from './EvaluationTab';
import ResendRecommendationsTab from './ResendRecommendationsTab';

const TAB_NAMES = ['campaigns', 'editor', 'history', 'evaluation', 'recommendations'] as const;
type TabName = (typeof TAB_NAMES)[number];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`plx-mailings-tabpanel-${index}`}
      aria-labelledby={`plx-mailings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const PlxMailings: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive tab state from URL
  const tabParam = searchParams.get('tab') || 'campaigns';
  const tabName = TAB_NAMES.includes(tabParam as TabName) ? (tabParam as TabName) : 'campaigns';
  const activeTab = TAB_NAMES.indexOf(tabName);
  const editingCampaignId =
    tabName === 'editor'
      ? searchParams.get('id')
        ? parseInt(searchParams.get('id')!, 10)
        : null
      : null;

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    const newTabName = TAB_NAMES[newValue];
    const params = new URLSearchParams();
    params.set('tab', newTabName);
    setSearchParams(params);
  };

  const handleEditCampaign = (campaignId: number) => {
    setSearchParams({ tab: 'editor', id: String(campaignId) });
  };

  const handleNewCampaign = () => {
    setSearchParams({ tab: 'editor' });
  };

  const handleCampaignSaved = () => {
    setSearchParams({ tab: 'campaigns' });
  };

  const handleResendFromRecommendation = (campaignId: number, suggestedSendAt: string) => {
    setSearchParams({
      tab: 'editor',
      id: String(campaignId),
      resend_to: suggestedSendAt,
    });
  };

  return (
    <Box m="20px">
      <Header title="Direkt-Mailings" subtitle="Produkt-Mailings direkt an Kunden versenden" />

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="plx mailings tabs">
          <Tab label="Kampagnen" />
          <Tab label={editingCampaignId ? 'Kampagne bearbeiten' : 'Neue Kampagne'} />
          <Tab label="Verlauf" />
          <Tab label="Auswertung" />
          <Tab label="Resend-Empfehlungen" />
        </Tabs>
      </Box>

      <TabPanel value={activeTab} index={0}>
        <CampaignList onEdit={handleEditCampaign} onNew={handleNewCampaign} />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <CampaignEditor
          campaignId={editingCampaignId}
          onSaved={handleCampaignSaved}
          onCancel={() => setSearchParams({ tab: 'campaigns' })}
        />
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <HistoryTab />
      </TabPanel>

      <TabPanel value={activeTab} index={3}>
        <EvaluationTab />
      </TabPanel>

      <TabPanel value={activeTab} index={4}>
        <ResendRecommendationsTab
          onResendClick={handleResendFromRecommendation}
          onOpenCampaign={handleEditCampaign}
        />
      </TabPanel>
    </Box>
  );
};

export default PlxMailings;
